import { useCallback, useEffect, useState } from 'react'
import { Card, Button } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { AlertTriangle, Check, Eye, History } from 'lucide-react'

/** Mängel aus den Checklisten (Vorlesemodus „Mangel“): offene Einträge mit „Erledigt“ / „Zur Kenntnis genommen“ und optionalem
 *  Kommentar. Bearbeitete Mängel verschwinden aus dem Handlungsbedarf, bleiben aber in der Historie sichtbar. */
export interface Mangel {
  item_id: number
  punkt: string
  notiz: string
  status: 'offen' | 'erledigt' | 'kenntnis'
  am: string | null
  von: string | null
  einsatz_id: number | null
  einsatz_titel: string | null
  flug_id: number | null
  checkliste: string
  bearbeitet_von: string | null
  bearbeitet_am: string | null
  kommentar: string | null
}

const STATUS_TEXT: Record<string, string> = { offen: 'Offen', erledigt: 'Erledigt', kenntnis: 'Zur Kenntnis genommen' }
const zeit = (s: string | null) => (s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—')

/** Anzahl offener Mängel (für den Zähler im Handlungsbedarf); null solange nicht geladen */
export function useMaengel() {
  const [offen, setOffen] = useState<Mangel[] | null>(null)
  const [historie, setHistorie] = useState<Mangel[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const laden = useCallback(async () => {
    try {
      const r = await api.get('/maengel', { params: { status: 'alle', limit: 100 } })
      const alle = ((r.data as { items?: Mangel[] })?.items ?? [])
      const gl = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
      const o = alle.filter(m => m.status === 'offen'), h = alle.filter(m => m.status !== 'offen')
      setOffen(alt => (alt && gl(alt, o)) ? alt : o)
      setHistorie(alt => gl(alt, h) ? alt : h)
      setFehler(null)
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Mängel konnten nicht geladen werden')
    }
  }, [])
  useEffect(() => { void laden() }, [laden])
  useEffect(() => {
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') void laden() }, 60000)
    return () => window.clearInterval(t)
  }, [laden])
  return { offen, historie, fehler, laden }
}

export function MaengelKarte({ offen, historie, fehler, onGeaendert }: { offen: Mangel[]; historie: Mangel[]; fehler: string | null; onGeaendert: () => void }) {
  const [kommentare, setKommentare] = useState<Record<number, string>>({})
  const [arbeitet, setArbeitet] = useState<number | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [zeigeHistorie, setZeigeHistorie] = useState(false)

  async function bearbeite(m: Mangel, status: 'erledigt' | 'kenntnis') {
    setArbeitet(m.item_id); setMeldung(null)
    try {
      await api.put(`/maengel/${m.item_id}`, { status, kommentar: (kommentare[m.item_id] || '').trim() || null })
      onGeaendert()
    } catch (e: any) {
      setMeldung(e?.response?.data?.detail || 'Speichern fehlgeschlagen – bitte erneut versuchen.')
    } finally { setArbeitet(null) }
  }

  if (!offen.length && !historie.length && !fehler) return null
  return (
    <Card className="p-4" data-testid="maengel-karte">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold text-dark-text"><AlertTriangle className="h-5 w-5 text-amber-400" />Mängel aus Checklisten ({offen.length} offen)</h3>
        {historie.length > 0 && (
          <Button size="sm" variant="secondary" className="min-h-[44px]" onClick={() => setZeigeHistorie(v => !v)} aria-expanded={zeigeHistorie} data-testid="maengel-historie-knopf">
            <History className="mr-1 h-4 w-4" />Historie ({historie.length})
          </Button>
        )}
      </div>
      {fehler && <p role="alert" className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">{fehler}</p>}
      {meldung && <p role="alert" className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">{meldung}</p>}
      {offen.length === 0 ? <p className="text-sm text-dark-text-muted">Keine offenen Mängel.</p> : (
        <ul className="space-y-3">
          {offen.map(m => (
            <li key={m.item_id} className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3" data-testid="mangel-eintrag">
              <p className="break-words text-sm font-medium text-dark-text">Mangel: {m.punkt} – {m.notiz}</p>
              <p className="mt-0.5 text-xs text-dark-text-muted">
                {[m.einsatz_titel ? `Einsatz „${m.einsatz_titel}“` : null, m.checkliste, m.von ? `von ${m.von}` : null, zeit(m.am)].filter(Boolean).join(' · ')}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input type="text" className="input min-h-[44px] min-w-[12rem] flex-1" placeholder="Kommentar (optional)" aria-label={`Kommentar zu ${m.punkt}`}
                  value={kommentare[m.item_id] ?? ''} onChange={e => setKommentare({ ...kommentare, [m.item_id]: e.target.value })} />
                <button type="button" disabled={arbeitet === m.item_id} onClick={() => bearbeite(m, 'erledigt')} data-testid="mangel-erledigt"
                  className="inline-flex min-h-[44px] items-center rounded-lg border border-green-500/60 bg-green-600/20 px-4 text-sm font-medium text-green-200 hover:bg-green-600/30 disabled:opacity-60"><Check className="mr-1 h-4 w-4" />Erledigt</button>
                <Button variant="secondary" className="min-h-[44px]" disabled={arbeitet === m.item_id} onClick={() => bearbeite(m, 'kenntnis')} data-testid="mangel-kenntnis"><Eye className="mr-1 h-4 w-4" />Zur Kenntnis genommen</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {zeigeHistorie && (
        <ul className="mt-4 space-y-2 border-t border-dark-border pt-3" data-testid="maengel-historie">
          {historie.map(m => (
            <li key={m.item_id} className="text-sm text-dark-text-muted">
              <span className="text-dark-text">{m.punkt} – {m.notiz}</span>
              <span className="block text-xs">{[m.einsatz_titel ? `Einsatz „${m.einsatz_titel}“` : null, m.checkliste, m.von ? `von ${m.von}` : null, zeit(m.am)].filter(Boolean).join(' · ')}</span>
              <span className="block text-xs">{STATUS_TEXT[m.status]} {m.bearbeitet_von ? `von ${m.bearbeitet_von}` : ''} {zeit(m.bearbeitet_am)}{m.kommentar ? ` – „${m.kommentar}“` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
